import { OrderModel } from "../../../screens/portfolio-info/types/OrderModel";
import { PortfolioModel } from "../../../screens/portfolio-info/types/PortfolioModel";
import { PositionModel } from "../../../screens/portfolio-info/types/PositionModel";
import { assetService } from "../AssetService";
import { portfolioMappers } from "../../mappers/PortfolioMappers";
import { portfolioCalculationService } from "../portfolio-calculation-service/PortfolioCalculationService";
import { OrderState } from "../../types/OrderState";
import { AssetType } from "../../types/AssetType";

export interface PortfolioBalanceValuationContext {
    underlyingPrice: number;
    valuationDate: Date;
    useCustomImpliedVolatility: boolean;
}

export interface PortfolioItemValuation {
    markPrice: number;
    delta: number;
    gamma: number | null;
    vega: number | null;
    theta: number | null;
}

class PortfolioBalanceCalculationService {
    getPositionValuation(
        position: PositionModel,
        valuationContext: PortfolioBalanceValuationContext | null,
    ): PortfolioItemValuation {
        return this.calculateValuation(
            position.type,
            position.strike,
            position.expireAt,
            position.size,
            position.impliedVolatility,
            position.customImpliedVolatility,
            {
                markPrice: position.markPrice,
                delta: position.delta,
                gamma: position.gamma,
                vega: position.vega,
                theta: position.theta,
            },
            assetService.isPositionOption(position) &&
                assetService.isPositionOptionExpired(position),
            valuationContext,
        );
    }

    getOrderValuation(
        order: OrderModel,
        valuationContext: PortfolioBalanceValuationContext | null,
    ): PortfolioItemValuation {
        return this.calculateValuation(
            order.type,
            order.strike,
            order.expireAt,
            order.size,
            order.markImpliedVolatility,
            order.customImpliedVolatility,
            {
                markPrice: order.markPrice,
                delta: order.delta,
                gamma: order.gamma,
                vega: order.vega,
                theta: order.theta,
            },
            assetService.isOption(order) && assetService.isOptionExpired(order),
            valuationContext,
        );
    }

    calculatePositionBalance(
        position: PositionModel,
        valuation: PortfolioItemValuation,
    ): number {
        return assetService.isPositionOption(position) &&
            assetService.isPositionOptionExpired(position)
                ? portfolioCalculationService.balance.calculatePositionBalanceAfterExpiration(position)
                : valuation.markPrice * position.size + position.volume;
    }

    calculateOrderBalance(
        order: OrderModel,
        valuation: PortfolioItemValuation,
    ): number {
        return assetService.isOption(order) && assetService.isOptionExpired(order)
            ? portfolioCalculationService.balance.calculateOrderBalanceAfterExpiration(order)
            : (valuation.markPrice - order.openPrice) * order.size;
    }

    calculateAtDate(
        portfolio: PortfolioModel,
        valuationContext: PortfolioBalanceValuationContext | null,
        includeFeesInBalance: boolean = false,
    ): number {
        const positionsBalance = portfolio.positions.reduce((balance, position) => {
            const valuation = this.getPositionValuation(position, valuationContext);
            return balance + this.calculatePositionBalance(position, valuation);
        }, 0);

        return portfolio.orders
            .filter(order => order.state === OrderState.Active)
            .reduce((balance, order) => {
                const valuation = this.getOrderValuation(order, valuationContext);
                return balance + this.calculateOrderBalance(order, valuation);
            }, positionsBalance) + (includeFeesInBalance ? this.calculateFees(portfolio) : 0);
    }

    calculateAtExpiration(portfolio: PortfolioModel, underlyingPrice: number, includeFeesInBalance: boolean = false): number {
        const positionsBalance = portfolio.positions.reduce(
            (balance, position) => balance + this.calculatePositionBalanceAtExpiration(
                position,
                underlyingPrice,
            ),
            0,
        );

        return portfolio.orders
            .filter(order => order.state === OrderState.Active)
            .reduce(
                (balance, order) => balance + this.calculateOrderBalanceAtExpiration(
                    order,
                    underlyingPrice,
                ),
                positionsBalance,
            ) + (includeFeesInBalance ? this.calculateFees(portfolio) : 0);
    }

    calculateFees(portfolio: PortfolioModel): number {
        const positionCents = portfolio.positions.reduce(
            (sum, position) => sum + Math.round((position.fee ?? 0) * 100), 0);
        return portfolio.orders
            .filter(order => order.state === OrderState.Active)
            .reduce((sum, order) => sum + Math.round((order.fee ?? 0) * 100), positionCents) / 100;
    }

    private calculateValuation(
        type: AssetType,
        strike: number | null,
        expireAt: Date | null,
        size: number,
        impliedVolatility: number | null,
        customImpliedVolatility: number | null,
        cachedValuation: PortfolioItemValuation,
        isExpired: boolean,
        valuationContext: PortfolioBalanceValuationContext | null,
    ): PortfolioItemValuation {
        if (valuationContext === null || isExpired) {
            return cachedValuation;
        }

        const effectiveImpliedVolatility = valuationContext.useCustomImpliedVolatility
            ? customImpliedVolatility ?? impliedVolatility
            : impliedVolatility;

        if (assetService.isOptionType(type) && effectiveImpliedVolatility === null) {
            return cachedValuation;
        }

        const { price, delta, gamma, vega, theta } = portfolioMappers.getAssetParameters(
            type,
            strike,
            expireAt,
            effectiveImpliedVolatility,
            null,
            valuationContext.underlyingPrice,
            valuationContext.valuationDate,
        );

        return {
            markPrice: price,
            delta: delta * size,
            gamma: gamma !== null ? gamma * size : null,
            vega: vega !== null ? vega * size : null,
            theta: theta !== null ? theta * size : null,
        };
    }

    private calculatePositionBalanceAtExpiration(
        position: PositionModel,
        underlyingPrice: number,
    ): number {
        if (assetService.isPositionOption(position) &&
            assetService.isPositionOptionExpired(position)
        ) {
            return portfolioCalculationService.balance.calculatePositionBalanceAfterExpiration(position);
        }

        return position.volume + position.size * this.getExpirationAssetPrice(
            position.type,
            position.strike,
            underlyingPrice,
        );
    }

    private calculateOrderBalanceAtExpiration(
        order: OrderModel,
        underlyingPrice: number,
    ): number {
        if (assetService.isOption(order) && assetService.isOptionExpired(order)) {
            return portfolioCalculationService.balance.calculateOrderBalanceAfterExpiration(order);
        }

        const expirationAssetPrice = this.getExpirationAssetPrice(
            order.type,
            order.strike,
            underlyingPrice,
        );

        return (expirationAssetPrice - order.openPrice) * order.size;
    }

    private getExpirationAssetPrice(
        type: AssetType,
        strike: number | null,
        underlyingPrice: number,
    ): number {
        if (type === AssetType.Spot || type === AssetType.Futures) {
            return underlyingPrice;
        }

        if (strike === null) {
            throw new Error("Option strike is null.");
        }

        if (type === AssetType.Call) {
            return Math.max(underlyingPrice - strike, 0);
        }

        if (type === AssetType.Put) {
            return Math.max(strike - underlyingPrice, 0);
        }

        throw new Error("Unknown AssetType");
    }
}

export const portfolioBalanceCalculationService = new PortfolioBalanceCalculationService();
